import React, { useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  BuildingStorefrontIcon, 
  UsersIcon, 
  ChartBarIcon, 
  ArrowLeftOnRectangleIcon,
  FlagIcon,
  BanknotesIcon,
  DocumentTextIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const menuItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: ChartBarIcon },
    { name: 'Salons', path: '/admin/salons', icon: BuildingStorefrontIcon },
    { name: 'Utilisateurs', path: '/admin/users', icon: UsersIcon },
    { name: 'Revenus', path: '/admin/payments', icon: BanknotesIcon },
    { name: 'Signalements', path: '/admin/reports', icon: FlagIcon },
    { name: 'Journal', path: '/admin/audit-logs', icon: DocumentTextIcon },
  ];

  const SidebarContent = () => (
    <>
      <div className="p-5 lg:p-6">
        <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-1">
          Re-Glow <span style={{ color: '#B58A5A' }}>Admin</span>
        </h1>
      </div>

      <nav className="flex-1 px-3 lg:px-4 space-y-1 mt-2 lg:mt-4">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-3 px-2 text-gray-400">Plateforme</p>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-md transition-colors duration-200 ${
                isActive 
                  ? 'text-white font-medium shadow-sm' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              style={{ backgroundColor: isActive ? '#B58A5A' : '' }}
            >
              <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-400'}`} />
              <span className="text-sm">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t" style={{ borderColor: '#1f2937' }}>
        <div className="flex items-center space-x-3 mb-4 px-2">
          <div className="h-8 w-8 rounded-md flex items-center justify-center font-bold text-sm text-white flex-shrink-0" style={{ backgroundColor: '#B58A5A' }}>
            {user.name.charAt(0)}
          </div>
          <div className="overflow-hidden">
            <p className="text-xs font-semibold text-white truncate">{user.name}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider truncate">Super Admin</p>
          </div>
        </div>
        
        <button
          onClick={async () => {
            await logout();
            window.location.href = '/login';
          }}
          className="flex items-center space-x-3 w-full px-3 py-2 text-gray-400 hover:bg-white/5 hover:text-white rounded-md transition-colors duration-200 font-medium"
        >
          <ArrowLeftOnRectangleIcon className="h-5 w-5" />
          <span className="text-sm">Déconnexion</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col lg:flex-row min-h-screen font-sans" style={{ backgroundColor: '#F8F6F2' }}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-64 border-r flex-col fixed inset-y-0 left-0 z-40" style={{ backgroundColor: '#111827', borderColor: '#1f2937' }}>
        <SidebarContent />
      </div>

      {/* Mobile Top Bar */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#111827] border-b border-[#1f2937] z-30 sticky top-0">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="p-2 -ml-2 text-gray-400 hover:bg-white/10 rounded-xl transition-colors"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <Link to="/admin/dashboard" className="text-lg font-bold text-white tracking-tighter flex items-center gap-1">
            Re-Glow<span style={{ color: '#B58A5A' }}>.</span> <span className="text-xs text-gray-400 font-normal ml-1">Admin</span>
          </Link>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm" 
            onClick={() => setIsMobileMenuOpen(false)} 
          />
          {/* Drawer */}
          <div 
            className="fixed inset-y-0 left-0 w-72 z-50 lg:hidden flex flex-col animate-slide-in-left"
            style={{ backgroundColor: '#111827' }}
          >
            <div className="absolute top-4 right-4">
              <button 
                onClick={() => setIsMobileMenuOpen(false)} 
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent />
          </div>
        </>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto lg:ml-64">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
